/**
 * MapModal - Modal for displaying Google Map
 *
 * Opens when user clicks "View Map" button on the location badge.
 * Shows the event location with a marker on the map.
 */
import Modal from "@ui-kit/overlays/Modal/Modal.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import Icon from "@ui-kit/icons/Icon.web";
import { useTranslation } from "@shared/utils/internationalization";
import { useBreakpointValue } from "@shared/hooks/useBreakpointValue";
import Button from "@shared/components/ui-kit/form/Button/Button.web";
import type { ModalSize } from "@ui-kit/overlays/Modal/Modal.props";
import { GoogleMapContainer } from "./GoogleMapContainer.web";

export interface MapModalProps {
  isOpen: boolean;
  onClose: () => void;
  latLng: string;
  locationName?: string;
  address?: string;
}

export function MapModal({
  isOpen,
  onClose,
  latLng,
  locationName,
  address,
}: MapModalProps): JSX.Element {
  const { t } = useTranslation("purchases");
  const size = useBreakpointValue<ModalSize>({ base: "full", sm: "4xl" });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={size} isCentered>
      <Modal.Header onClose={onClose}>{t("Location")}</Modal.Header>
      <Modal.Body>
        {/* Location info */}
        {(locationName || address) && (
          <Flex gap="sm" alignItems="flex-start" pb="md">
            <Icon.Location
              boxSize="element.xl"
              colorPalette="primary"
              flexShrink={0}
            />
            <Flex direction="column" fontSize="sm" color="fg">
              {locationName && <Text fontWeight="medium">{locationName}</Text>}
              {address && <Text color="fg.muted">{address}</Text>}
            </Flex>
          </Flex>
        )}

        <GoogleMapContainer latLngPosition={latLng} displayMarker />
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClose}>{t("Close")}</Button>
      </Modal.Footer>
    </Modal>
  );
}

export default MapModal;
